import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import {
  AIASSIST_FIELDS_ENTITY_NAME_LABEL_PAIR,
  CONDITIONAL_FIELDS_ENTITY_NAME,
  CONDITIONAL_FIELDS_ENTITY_NAME_LABEL_PAIR,
  DOCUMENT_SENDER_ENTITY_LABEL_PAIR,
  EntityTypeLabelPair,
  IFieldMeta,
  IMAGE_FIELDS_ENTITY_NAME_LABEL_PAIR,
  SCRIPT_FIELDS_ENTITY_NAME_LABEL_PAIR,
} from '../../interfaces/imetadata';
import { SignatureFieldContext } from '../../interfaces/signature';
import { FieldContext } from '../../utils/fieldcontext';
import { templateContext } from '../../utils/template-context';
import { ContentControlToFieldMapService, SerializedFieldContext } from '../content-control-to-field-map.service';
import { ContentControlService } from '../content-control.service';
import { ControllerService } from '../controller.service';
import { ActiveContentControlMonitor } from '../document-monitors/active-content-control-monitor';
import { ContentControlId } from '../document-monitors/content-control-id';
import { ContentControlMonitor } from '../document-monitors/content-control-monitor';
import { ContentControlToSignatureFieldService } from '../esignature/content-control-to-signature-field.service';
import { SignatureControllerService } from '../esignature/signature-controller.service';
import { LOCALSTORAGE_COPY_KEY } from './copy-key';
import { CopyPasteStorageService } from './copy-paste-storage.service';
import { CopyService } from './copy.service';
import { ControlFieldPairDTO, FieldDTO } from './field-dto';
import { FieldType } from './field-type';
import { PasteLocation, PasteLocationEnum } from './paste-location';
import { pasteRuleTable } from './paste-rules/paste-rule-table';
import { InvalidStateEvent, ValidStateChangeEvent, ValidStateEmitter, ValidStateEvent } from './valid-state-emitter';

@Injectable({ providedIn: 'root' })
export class PasteService {
  private pasteEmitter: ValidStateEmitter = new ValidStateEmitter();

  private pasteActionSubject = new Subject<void>();
  private pasteActionImpl$ = this.pasteActionSubject.asObservable();

  constructor(
    private activeContentControlMonitor: ActiveContentControlMonitor,
    private contentControlMonitor: ContentControlMonitor,
    private contentControlService: ContentControlService,
    private contentControlToFieldMapService: ContentControlToFieldMapService,
    private contentControlToSignatureFieldService: ContentControlToSignatureFieldService,
    private controllerService: ControllerService,
    private signatureControllerService: SignatureControllerService,
    private copyPasteStorage: CopyPasteStorageService,
    copyService: CopyService
  ) {
    activeContentControlMonitor.activeContentControlChanged$.subscribe((activeContentControl) =>
      this.activeControlChanged(activeContentControl)
    );
    copyService.copyAction$.subscribe((_) => this.updatePasteState());
    // this will only pick up events from *other* instances of the Word AddIn
    window.addEventListener('storage', (event: StorageEvent) => this.storageEventHandler(event));
  }

  get pasteValidStateChange$(): Observable<ValidStateChangeEvent> {
    return this.pasteEmitter.validStateChange$;
  }

  get pasteAction$(): Observable<void> {
    return this.pasteActionImpl$;
  }

  public doPaste(): void {
    (async () => {
      const fieldDTOJson = await this.copyPasteStorage.getItem(LOCALSTORAGE_COPY_KEY);
      if (!fieldDTOJson) {
        // TODO: maybe error message to user?
        console.error('Paste called but no DTO copy data.');
        return;
      }

      const fieldDTO = FieldDTO.fromJson(fieldDTOJson);

      // TODO: when we can, use context.document.onContentControlAdded.add(handler)
      const subscription = this.contentControlMonitor.contentControlsAdded$.subscribe({
        next: (contentControlsAdded: ContentControlId[]) => {
          subscription.unsubscribe();

          const rootControls = fieldDTO.getRootLevelControlFieldPairs();
          (async () => {
            // we could probably get away with doing these in parallel, but just to be safe do them sequentially
            for (const rootControl of rootControls) {
              await this.insert(rootControl, fieldDTO.controlFieldPairList, contentControlsAdded);
            }
          })();
        },
      });

      this.firePasteAction();

      Word.run(async (context) => {
        await this.contentControlService.insertFromPaste(context, fieldDTO.ooxml);
      });
    })();
  }

  private firePasteAction(): void {
    this.pasteActionSubject.next();
  }

  private async insert(
    rootControlDto: ControlFieldPairDTO,
    fieldsCopied: ControlFieldPairDTO[],
    contentControlsAdded: ContentControlId[]
  ): Promise<void> {
    const stack = [rootControlDto];
    while (stack.length > 0) {
      const controlFieldPair = stack.pop()!;
      controlFieldPair.contentControlDto.childIds
        .reverse()
        .forEach((childId) => stack.push(fieldsCopied.find((mf) => mf.contentControlDto.id === childId)!));

      console.debug('Paste process:', controlFieldPair);

      const addedContentControl = contentControlsAdded.find((cca) => cca.tag === controlFieldPair.contentControlDto.tag);
      if (!addedContentControl) {
        throw new Error('Could not find matching content control on paste.');
      }

      await Word.run(async (context) => {
        if (controlFieldPair.field.type === FieldType.Signature) {
          const sfc = <SignatureFieldContext>controlFieldPair.field.context;
          sfc.contentControlId = addedContentControl.id;
          await this.signatureControllerService.insert(context, sfc);
        } else {
          const sfc = <SerializedFieldContext>controlFieldPair.field.context;
          const { entity, field } = this.buildEntityAndField(controlFieldPair.field.type, sfc);

          if (!field) {
            throw new Error(`Could not find field for ${sfc.path}`);
          }

          const fieldContext = new FieldContext(
            sfc.namespace,
            entity,
            field,
            sfc.conditions,
            sfc.dateFormatOptions,
            sfc.integerFormat,
            sfc.decimalFormat,
            sfc.sortOption,
            sfc.limit,
            sfc.defaultEmptyValue,
            sfc.stringFormat,
            sfc.useDisplayValue,
            sfc.imageFieldConfig,
            sfc.aiAssistFieldConfig
          );
          await this.controllerService.insertFromPaste(context, fieldContext, addedContentControl.id);
        }

        // TODO: try and batch these up - move the run outside the traverse and pass the context in - are all parent controls present?
        await context.sync();
      });
    }
  }

  private buildEntityAndField(
    fieldType: FieldType,
    sfc: SerializedFieldContext
  ): { entity: EntityTypeLabelPair; field: IFieldMeta | undefined } {
    let field: IFieldMeta | undefined;
    let entity: EntityTypeLabelPair = {
      entity: templateContext.getTargetEntity().entity,
      label: templateContext.getTargetEntity().label,
    };
    if (fieldType === FieldType.Entity) {
      field = templateContext.getFieldByPath(sfc.path);
    } else if (fieldType === FieldType.Script) {
      entity = SCRIPT_FIELDS_ENTITY_NAME_LABEL_PAIR;
      field = { namespace: sfc.namespace, path: sfc.path, label: '', type: sfc.dataType! };
    } else if (fieldType === FieldType.DocumentSender) {
      entity = DOCUMENT_SENDER_ENTITY_LABEL_PAIR;
      field = { namespace: sfc.namespace, path: sfc.path, label: '', type: sfc.dataType! };
    } else if (fieldType === FieldType.Image) {
      entity = IMAGE_FIELDS_ENTITY_NAME_LABEL_PAIR;
      field = {
        namespace: sfc.namespace,
        path: sfc.path,
        label: sfc.imageFieldConfig!.type.toUpperCase(),
        type: sfc.imageFieldConfig!.type,
      };
    } else if (fieldType === FieldType.Conditional) {
      entity = CONDITIONAL_FIELDS_ENTITY_NAME_LABEL_PAIR;
      field = { namespace: sfc.namespace, path: sfc.path, label: '', type: sfc.dataType! };
    } else if (fieldType === FieldType.AiAssist) {
      entity = AIASSIST_FIELDS_ENTITY_NAME_LABEL_PAIR;
      field = { namespace: sfc.namespace, path: sfc.path, label: '', type: sfc.dataType! };
    } else {
      throw new Error(`Unexpected field type '${fieldType}'.`);
    }

    return { entity, field };
  }

  private activeControlChanged(activeControl: ContentControlId | undefined): void {
    console.debug('%ccopy-n-paste - active content control changed handler', 'color:#aaaa00');
    this.updatePasteState();
  }

  private storageEventHandler(event: StorageEvent): void {
    if (event.key === LOCALSTORAGE_COPY_KEY) {
      // Local storage is used as an event emitter between tabs, as IndexedDB doesn't support events
      console.log('Received storage event for copy-paste service with key: ', event.key);
      this.updatePasteState();
    }
  }

  private async updatePasteState(): Promise<void> {
    const fieldDTOJson = await this.copyPasteStorage.getItem(LOCALSTORAGE_COPY_KEY);
    if (!fieldDTOJson) {
      this.pasteEmitter.fireValidStateChange(InvalidStateEvent);
      return;
    }

    const fieldDTO = FieldDTO.fromJson(fieldDTOJson);

    Word.run(async (ctx) => {
      const activeContentControl = await this.activeContentControlMonitor.getActiveContentControl(ctx);
      const pasteLocation = this.getPasteLocation(activeContentControl?.id);

      const rootFields = fieldDTO.getRootLevelControlFieldPairs();

      const canPaste = rootFields.every((fieldToPaste) =>
        pasteRuleTable[fieldToPaste.field.type][pasteLocation.type].canPaste(pasteLocation, fieldToPaste.field.context)
      );
      const pasteValidStateEvent = canPaste ? ValidStateEvent : InvalidStateEvent;
      this.pasteEmitter.fireValidStateChange(pasteValidStateEvent);
    });
  }

  private getPasteLocation(activeContentControlId: number | undefined): PasteLocation {
    if (!activeContentControlId) {
      return { type: PasteLocationEnum.Document, location: undefined };
    }

    const dataField = this.contentControlToFieldMapService.get(activeContentControlId);
    if (dataField) {
      if (dataField.entity === CONDITIONAL_FIELDS_ENTITY_NAME) {
        return {
          type: PasteLocationEnum.ConditionalField,
          location: dataField,
        };
      }
      return { type: PasteLocationEnum.DataField, location: dataField };
    }

    const signatureField = this.contentControlToSignatureFieldService.get(activeContentControlId);
    if (signatureField) {
      return {
        type: PasteLocationEnum.SignatureField,
        location: signatureField,
      };
    }

    return { type: PasteLocationEnum.Document, location: undefined };
  }
}
